import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Upload } from "lucide-react";

export default function Candidates() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [cands, setCands] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [stages, setStages] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [reqFilter, setReqFilter] = useState(params.get("requisition") || "");
  const [sourceFilter, setSourceFilter] = useState(params.get("source") || "");
  const stageFilter = params.get("stage") || "";
  const [form, setForm] = useState({ name: "", email: "", phone: "", requisition_id: "", source: "portal", resume_text: "", experience_years: 0 });
  const [parsing, setParsing] = useState(false);

  const uploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/parse-resume", fd);
      setForm((f) => ({ ...f, resume_text: data.text }));
      toast.success(`Resume parsed — ${data.chars.toLocaleString()} characters`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setParsing(false);
      e.target.value = "";
    }
  };

  const load = () => api.get("/candidates").then((r) => setCands(r.data));
  useEffect(() => {
    load();
    api.get("/requisitions").then((r) => {
      setReqs(r.data);
      const open = r.data.filter((q) => q.status === "open");
      if (open[0]) setForm((f) => ({ ...f, requisition_id: open[0].id }));
    });
    api.get("/candidates/stages").then((r) => setStages(r.data)).catch(() => {});
    api.get("/sourcing/connectors").then((r) => {
      const list = r.data.filter((c) => c.supports_import !== false);
      setConnectors(list);
      if (list[0]) setForm((f) => ({ ...f, source: list[0].name }));
    }).catch(() => {});
  }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.requisition_id) return toast.error("Name, email and requisition are required");
    try {
      await api.post("/candidates", { ...form, experience_years: +form.experience_years });
      toast.success("Candidate added");
      setShowForm(false);
      setForm((f) => ({ ...f, name: "", email: "", phone: "", resume_text: "" }));
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const isCandidate = user?.role === "candidate";

  const sources = useMemo(() => [...new Set(cands.map((c) => c.source).filter(Boolean))], [cands]);
  const filtered = useMemo(() => cands.filter((c) => {
    if (search.trim()) {
      const q = search.toLowerCase();
      if (!`${c.name} ${c.email} ${c.resume_text || ""}`.toLowerCase().includes(q)) return false;
    }
    if (reqFilter && c.requisition_id !== reqFilter) return false;
    if (sourceFilter && c.source !== sourceFilter) return false;
    return true;
  }), [cands, search, reqFilter, sourceFilter]);
  const stageKeys = stages.map((s) => s.key);
  const visibleStages = stageFilter ? stageKeys.filter((s) => s === stageFilter) : stageKeys;
  const stageMeta = (k) => stages.find((s) => s.key === k) || { label: k, color: "#94A3B8" };

  return (
    <div data-testid="candidates-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">{isCandidate ? "Your journey" : "Talent Pipeline"}</div>
          <h1 className="font-heading font-black text-3xl tracking-tight mt-1">{isCandidate ? "My Applications" : "Candidates"}</h1>
        </div>
        {!isCandidate && (
          <button data-testid="add-candidate-button" onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-[#0A251D] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">
            <Plus size={14} /> Add Candidate
          </button>
        )}
      </div>

      {showForm && (
        <div className="border bg-white rounded-xl p-6 mt-5" data-testid="candidate-form">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Full Name *</label>
              <input data-testid="cand-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Email *</label>
              <input data-testid="cand-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Requisition *</label>
              <select data-testid="cand-req-select" value={form.requisition_id} onChange={(e) => setForm({ ...form, requisition_id: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm bg-white">
                {reqs.filter((r) => r.status === "open").map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Source</label>
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm bg-white">
                {connectors.map((c) => <option key={c.name} value={c.name}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Experience (yrs)</label>
              <input type="number" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} className="mt-1 w-full border px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="mt-4">
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold">Resume (upload or paste — used for AI screening)</label>
              <label data-testid="cand-resume-upload" className={`flex items-center gap-1.5 text-xs font-semibold border px-3 py-1.5 rounded-lg cursor-pointer transition-colors duration-150 ${parsing ? "opacity-60" : "hover:border-[#E05A47] hover:text-[#E05A47]"}`}>
                <Upload size={12} /> {parsing ? "Extracting…" : "Upload PDF / DOCX / TXT"}
                <input type="file" accept=".pdf,.docx,.txt,.md" className="hidden" onChange={uploadResume} disabled={parsing} />
              </label>
            </div>
            <textarea data-testid="cand-resume-textarea" value={form.resume_text} onChange={(e) => setForm({ ...form, resume_text: e.target.value })} rows={5} className="mt-2 w-full border px-3 py-2 text-sm" />
          </div>
          <div className="mt-4 flex gap-3">
            <button data-testid="cand-save-button" onClick={create} className="bg-[#0A251D] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-widest hover:bg-[#133E31] transition-colors duration-150">Save Candidate</button>
            <button onClick={() => setShowForm(false)} className="border px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-secondary transition-colors duration-150">Cancel</button>
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3" data-testid="candidates-filter-bar">
        <input data-testid="candidates-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, skills…" className="border bg-white px-3 py-2 text-sm w-64 focus:outline-none focus:ring-2 focus:ring-[#E05A47]/60" />
        <select data-testid="candidates-req-filter" value={reqFilter} onChange={(e) => setReqFilter(e.target.value)} className="border bg-white px-3 py-2 text-sm">
          <option value="">Requisition: All</option>
          {reqs.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
        </select>
        <select data-testid="candidates-source-filter" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="border bg-white px-3 py-2 text-sm">
          <option value="">Source: All</option>
          {sources.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
        </select>
        {stageFilter && (
          <button data-testid="clear-stage-filter" onClick={() => setParams({})}
            className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider bg-[#0A251D] text-white px-3 py-1.5 rounded-full hover:bg-[#133E31] transition-colors duration-150">
            Stage: {stageFilter} ✕
          </button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} candidate(s)</span>
      </div>

      <div className="mt-4 overflow-x-auto pb-4">
        <div className="flex gap-3 min-w-max">
          {visibleStages.map((stage) => {
            const items = filtered.filter((c) => c.stage === stage);
            const meta = stageMeta(stage);
            return (
              <div key={stage} className="w-56 shrink-0 bg-white border border-t-4" style={{ borderTopColor: meta.color }} data-testid={`pipeline-column-${stage}`}>
                <div className="px-3 py-2.5 border-b flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-widest font-bold">{meta.label}</span>
                  <span className="text-xs font-bold text-muted-foreground">{items.length}</span>
                </div>
                <div className="p-2 space-y-2 min-h-[120px]">
                  {items.map((c) => (
                    <Link key={c.id} to={`/candidates/${c.id}`} data-testid={`candidate-card-${c.id}`}
                      className="block border bg-white rounded-xl p-3 hover:border-[#0A251D] transition-colors duration-150">
                      <div className="text-sm font-semibold truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{reqs.find((r) => r.id === c.requisition_id)?.title || c.source}</div>
                      {c.ai_screening && (
                        <div className="mt-1.5 text-xs font-bold text-[#0A251D]">AI {c.ai_screening.overall_score}/100</div>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
